import { gql } from '@nerdwallet/apollo';

const marketplaceEntityWithPlacementDrivers = gql`
  fragment PlacementDrivers on MarketplaceEntityWithPlacementInterface {
    placementDrivers {
      id
      value
      description
      label
      valueTooltip
      valueTooltipHasHtml
    }
  }
`;

const imageFields = gql`
  fragment ImageFields on EntityPlacementImage {
    alt
    height
    source
    width
    resizableImagePath
    sourceLarge
    sourceMedium
  }
`;

const ctaFields = gql`
  fragment CtaFields on EntityPlacementCTA {
    link
    showIcon
    subtext
    text
    type
  }
`;

const autoLoansOfferQuery = gql`
  fragment AutoLoansOffer on AutoLoansOfferV2 {
    analyticsProps {
      institutionId
      offerId
      productId
    }
    displayName
    reviewLink
    starRating
    starRatingTooltip
    starRatingLabel
    starRatingLabelTooltip
    monetizable
    cta {
      link
      text
      subtext
      showIcon
      type
    }
    driverEstimatedApr {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverLoanAmount {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverMinCreditScore {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    image {
      alt
      source
    }
    consBullets
    prosBullets
    tagline
    qualificationsBullets
    offerTags
    bestForLabel
  }
`;

const creditCardsOfferQuery = gql`
  fragment CreditCardsOffer on CreditCardsOfferV2 {
    id
    name
    status
    displayName
    entityType
    productType
    marketingBullets
    minFico
    maxFico
    limitedCreditHistory
    termsAndFeesLink
    rewardsRates {
      rate
      description
    }
    starRating
    starRatingTooltip
    reviewLink
    ratesAndFeesCta {
      link
      text
      showIcon
      type
      subtext
    }
    recommendedCreditScoreRangeDetails {
      creditScoreRange
      description
    }
    cta {
      ...CtaFields
    }
    phoneNumber
    image {
      ...ImageFields
    }
    product {
      name
      id
    }
    institution {
      name
      id
    }
    bestOfAwards {
      category
      year
    }
    structuredContentCons
    structuredContentPros
    slug
    monetizable
    recommendedCreditScoreRangeDetails {
      creditScoreRange
      description
    }

    # for rewards DRIVER_GROUP feature
    introPurchases
    introBalanceTransfers
    aprIntroMessageList

    driverAnnualFee {
      id
      description
      label
      value
    }

    driverIntroApr {
      id
      description
      label
      value
    }

    driverRegularApr {
      id
      description
      label
      value
    }
  }
`;

const bankingOfferQuery = gql`
  fragment BankingOffer on BankingOfferV2 {
    id
    displayName
    apy
    apyOverride
    apyAnnotation
    benefits
    bonusDetails
    bonusAmountOverride
    cdDepositTerm
    starRating
    federallyInsuredAnnotation
    entityType
    cta {
      ...CtaFields
    }
    product {
      name
      id
    }
    institution {
      name
      id
    }
    image {
      ...ImageFields
    }
    minimumBalance
    minOpeningDeposit
    driverApy {
      id
      value
      description
      label
      labelTooltip
      valueTooltip
    }
    driverApyWithOverride {
      id
      value
      description
      label
      labelTooltip
      valueTooltip
    }
    driverBonus {
      id
      value
      description
      label
      labelTooltip
      valueTooltip
    }
    driverBonusWithOverride {
      id
      value
      description
      label
      labelTooltip
      valueTooltip
    }
    driverMonthlyFee {
      id
      value
      description
      label
      labelTooltip
      valueTooltip
    }
    driverMinimumBalance {
      id
      value
      description
      label
      labelTooltip
      valueTooltip
    }
    structuredContentCons
    structuredContentPros
  }
`;

const investingOfferQuery = gql`
  fragment InvestingOffer on InvestingOfferV2 {
    id
    starRating
    displayName
    reviewedAtDate
    periodConsideredDate
    monetizable
    entityType
    cta {
      ...CtaFields
    }
    product {
      name
      id
    }
    institution {
      name
      id
    }
    image {
      ...ImageFields
    }
    structuredContentCons
    structuredContentPros
  }
`;

const studentLoansOfferQuery = gql`
  fragment EducationLoansOffer on EducationLoansOfferV2 {
    analyticsProps {
      institutionId
      offerId
      productId
    }
    availableTermLengths
    disclaimer
    bestForLabel
    displayName
    excludedStates
    minFico
    starRating
    starRatingTooltip
    starRatingLabel
    starRatingLabelTooltip
    reviewLink
    monetizable
    cta {
      link
      text
      subtext
      showIcon
      type
    }
    image {
      alt
      source
    }
    driverMinCreditScore {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverFixedAprWithAnnotation {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverVariableAprWithAnnotation {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    consBullets
    prosBullets
    qualificationsBullets
    tagline
    institution {
      name
    }
    partner
    partnerDirectLink
    product {
      name
      subType
    }
  }
`;

const personalLoansOfferQuery = gql`
  fragment PersonalLoansOffer on PersonalLoansOfferV2 {
    analyticsProps {
      institutionId
      offerId
      productId
    }
    id
    displayName
    excludedStates
    minApr
    maxApr
    minLoanAmount
    minLoanTermMonths
    maxLoanAmount
    monetizable
    starRating
    starRatingTooltip
    starRatingLabel
    starRatingLabelTooltip
    offerTags
    tagline
    prosBullets
    consBullets
    qualificationsBullets
    availableTermLengths
    feesBullets
    disclaimer
    reviewLink
    exclusiveDisclaimer
    cta {
      link
      text
      subtext
      showIcon
      type
    }
    image {
      alt
      source
      sourceMedium
      sourceLarge
    }
    driverEstimatedApr {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverLoanAmount {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverMinCreditScore {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
  }
`;

const mortgagesLoansOfferQuery = gql`
  fragment MortgagesOffer on MortgagesOfferV2 {
    id
    name
    monetizable
    analyticsProps {
      institutionId
      offerId
      productId
    }
    displayName
    excludedStates
    nmlsId
    starRating
    starRatingTooltip
    starRatingLabel
    starRatingLabelTooltip
    reviewLink
    image {
      alt
      source
    }
    cta {
      link
      subtext
      text
      showIcon
      type
    }
    driverMinCreditScore {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverMinDownPayment {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverNationalRegional {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    driverMaxLtv {
      label
      labelTooltip
      value
      valueTooltip
      valueTooltipHasHtml
    }
    drivers
    consBullets
    prosBullets
    tagline
    reviewLink
    entityType
  }
`;

// for home warranty and auto warranty products
const expansionOfferQuery = gql`
  fragment ExpansionOffer on ExpansionOfferV2 {
    applyLink
    bbbGradeDescription
    bonus
    bonusDetails
    consBullets
    ctaAnnotation
    depositRequiredDescription
    description
    displayName
    driver1
    driver1ExplanationText
    driver2
    driver2ExplanationText
    driver3
    driver3ExplanationText
    featuresOffered
    financingOptionsDescription
    fullServicePackingDescription
    homeTypesCovered
    image {
      alt
      height
      resizableImagePath
      source
      sourceLarge
      sourceMedium
      width
    }
    imageSource
    installationOptions
    institution {
      id
    }
    isMonetizing
    label
    monetizable
    name
    phoneNumber
    phoneNumberAnnotation
    plansAvailableDescription
    product {
      id
    }
    prosBullets
    responseTimeDescription
    reviewLink
    serviceAvailabilityDescription
    starRating
    starRatingTooltip
    statesCovered
    status
    systemAndAppliancesCovered
    tty
    updatedAt
    workingSchedule
    analyticsProps {
      offerId
      productId
      legacyAdditionalEventData
      legacySlug
      legacyVertical
    }
    cta {
      link
      subtext
    }
    showNameInComparisonTable
    structuredContentCons
    structuredContentPros
    __typename
  }
`;

const insuranceLoansOfferQuery = gql`
  fragment InsuranceOffer on InsuranceOfferV2 {
    id
    starRating
    displayName
    reviewLink
    product {
      name
      id
    }
    institution {
      name
      id
    }
    phoneNumber
    cta {
      ...CtaFields
    }
    image {
      ...ImageFields
    }
    structuredContentCons
    structuredContentPros
  }
`;

export const marketplaceOffers = gql`
  query MarketplaceEntitiesV2($ids: [ID!]) {
    marketplaceEntitiesV2(ids: $ids) {
      id
      name
      entityType
      productType
      status
      __typename
      ...CreditCardsOffer
      ...BankingOffer
      ...InvestingOffer
      ...EducationLoansOffer
      ...PersonalLoansOffer
      ...MortgagesOffer
      ...InsuranceOffer
      ...AutoLoansOffer
      ...ExpansionOffer
      ...PlacementDrivers
    }
  }
  ${ctaFields}
  ${imageFields}
  ${marketplaceEntityWithPlacementDrivers}
  ${creditCardsOfferQuery}
  ${bankingOfferQuery}
  ${investingOfferQuery}
  ${studentLoansOfferQuery}
  ${personalLoansOfferQuery}
  ${mortgagesLoansOfferQuery}
  ${insuranceLoansOfferQuery}
  ${autoLoansOfferQuery}
  ${expansionOfferQuery}
`;
